import type * as ReactAuthStorage from '@utility-nyc/react-auth-storage';
import type {
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import Axios, { AxiosError } from 'axios';

const SESSION_EXPIRED_STORAGE_KEY = 'south-street-session-expired';

const baseURL =
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  process.env.NEXT_PUBLIC_SDK_BASE_URL ||
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  process.env.EXPO_PUBLIC_SDK_BASE_URL ||
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  process.env.SDK_BASE_URL ||
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  process.env.NEXT_PUBLIC_BASE_URL ||
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  process.env.EXPO_PUBLIC_BASE_URL ||
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  process.env.BASE_URL;

const apiAxiosInstance = Axios.create({
  paramsSerializer: { indexes: null },
  baseURL,
});

const refreshAxiosInstance = Axios.create({ baseURL });

type AuthTokens = {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  twilioAccessToken?: string;
};

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _sessionRetry?: boolean;
};

type ApiErrorBody = {
  error?: {
    code?: string;
  };
};

const NON_REFRESHABLE_UNAUTHORIZED_CODES = new Set([
  'AuthLoginError',
  'EndUserBlockedAccess',
  'ExpireSessionAfterWorkingHoursError',
  'OutBusinessWorkingHoursError',
]);

class SessionChangedDuringRefreshError extends Error {}

let refreshRequest: Promise<AuthTokens> | null = null;

const isRefreshEndpoint = (url?: string) => url?.includes('/auth/refresh');

const getErrorCode = (error: AxiosError<ApiErrorBody>) => {
  const data = error.response?.data as ApiErrorBody | undefined;

  return data?.error?.code;
};

const isTerminalRefreshError = (error: unknown) => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  const status = error.response?.status;
  const code = getErrorCode(error);

  if (code === 'OutBusinessWorkingHoursError') {
    return false;
  }

  return status === 401 || status === 403 || status === 404;
};

const shouldAttemptSessionRefresh = (error: AxiosError<ApiErrorBody>) => {
  if (error.response?.status !== 401) {
    return false;
  }

  const errorCode = getErrorCode(error);

  return (
    Boolean(getAuthState().refreshToken) &&
    (!errorCode || !NON_REFRESHABLE_UNAUTHORIZED_CODES.has(errorCode))
  );
};

const getAuthState = () => {
  // Keep auth storage lazy so importing generated SDK types does not initialize
  // React Native storage in web and test environments.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useAuthStore } = require('@utility-nyc/react-auth-storage') as typeof ReactAuthStorage;

  return useAuthStore.getState();
};

const expireSession = () => {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, 'true');
  }

  getAuthState().handleLogout();
};

const refreshSession = (): Promise<AuthTokens> => {
  if (refreshRequest) {
    return refreshRequest;
  }

  const { refreshToken } = getAuthState();

  if (!refreshToken) {
    return Promise.reject(
      new Error('Cannot refresh a session without a refresh token.'),
    );
  }

  refreshRequest = refreshAxiosInstance
    .post<{ data?: { tokens?: AuthTokens } }>('/auth/refresh', undefined, {
      headers: { Authorization: `Bearer ${refreshToken}` },
    })
    .then(({ data }) => {
      const tokens = data.data?.tokens;

      if (!tokens?.accessToken) {
        throw new Error('Session refresh returned no access token.');
      }

      if (getAuthState().refreshToken !== refreshToken) {
        throw new SessionChangedDuringRefreshError(
          'The session changed while refresh was in progress.',
        );
      }

      getAuthState().setTokens(tokens);

      return tokens;
    })
    .catch((error) => {
      if (isTerminalRefreshError(error)) {
        expireSession();
      }

      throw error;
    })
    .finally(() => {
      refreshRequest = null;
    });

  return refreshRequest;
};

apiAxiosInstance.interceptors.request.use((requestConfig) => {
  const { accessToken, refreshToken } = getAuthState();
  const token = isRefreshEndpoint(requestConfig.url)
    ? refreshToken
    : accessToken;

  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }

  return requestConfig;
});

apiAxiosInstance.interceptors.response.use(
  (response) => {
    const tokens = response.data?.data?.tokens as AuthTokens | undefined;

    if (tokens?.accessToken) {
      getAuthState().setTokens(tokens);
    }

    return response;
  },
  async (error: AxiosError<ApiErrorBody>) => {
    const requestConfig = error.config as RetriableRequestConfig | undefined;

    if (!requestConfig) {
      throw error;
    }

    if (isRefreshEndpoint(requestConfig.url)) {
      if (isTerminalRefreshError(error)) {
        expireSession();
      }

      throw error;
    }

    if (!shouldAttemptSessionRefresh(error)) {
      throw error;
    }

    if (requestConfig._sessionRetry) {
      throw error;
    }

    requestConfig._sessionRetry = true;

    await refreshSession();

    return apiAxiosInstance.request(requestConfig);
  },
);

// add a second `options` argument here if you want to pass extra options to each generated query
const customAxios = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> =>
  apiAxiosInstance
    .request({
      ...config,
      ...options,
    })
    .then(({ data }) => data)
    .catch((e) => {
      throw e;
    });

type ErrorType<Error> = AxiosError<Error>;

export {
  apiAxiosInstance,
  customAxios,
  refreshAxiosInstance,
  refreshSession,
  SESSION_EXPIRED_STORAGE_KEY,
};
export type { ErrorType };
